import React, { useState, useCallback } from 'react';
import { View, Text, TouchableOpacity, Modal, StyleSheet } from 'react-native';

/**
 * NumpadModal - Tastierino numerico per inserimento diretto punteggio
 * 
 * Props:
 * - visible: boolean - se mostrare il modal
 * - onClose: () => void - callback per chiudere
 * - onConfirm: (value: number) => void - callback con valore confermato
 * - currentValue: number - valore corrente (per pre-fill)
 * - maxValue: number - valore massimo consentito
 * - teamId: 'A' | 'B' - identificativo squadra (per colore header)
 * - teamName: string - nome squadra (per header)
 * - color: string - colore primario
 */
const NumpadModal = ({
    visible = false,
    onClose,
    onConfirm,
    currentValue = 0,
    maxValue = 45,
    teamId = 'A',
    teamName = 'Squadra',
    color = '#1976d2',
}) => {
    // Stato input - inizia con stringa vuota in modalità sostituzione
    const [inputValue, setInputValue] = useState('');
    const [isReplaceMode, setIsReplaceMode] = useState(true);

    // Reset stato quando il modal si apre
    React.useEffect(() => {
        if (visible) {
            setInputValue('');
            setIsReplaceMode(true);
        }
    }, [visible]);

    // Gestione input cifra
    const handleDigit = useCallback((digit) => {
        if (isReplaceMode) {
            // Prima cifra: sostituisce il valore corrente
            setInputValue(digit.toString());
            setIsReplaceMode(false);
        } else {
            // Cifre successive: concatena
            const newValue = inputValue + digit.toString();
            // Limita a 3 cifre
            if (newValue.length <= 3) {
                setInputValue(newValue);
            }
        }
    }, [inputValue, isReplaceMode]);

    // Gestione cancella
    const handleDelete = useCallback(() => {
        if (inputValue.length > 0) {
            setInputValue(inputValue.slice(0, -1));
        }
        if (inputValue.length <= 1) {
            setIsReplaceMode(true);
        }
    }, [inputValue]);

    // Gestione conferma
    const handleConfirm = useCallback(() => {
        const numValue = inputValue === '' ? currentValue : parseInt(inputValue, 10);
        const clampedValue = Math.min(Math.max(0, numValue || 0), maxValue);
        onConfirm(clampedValue);
        onClose();
    }, [inputValue, currentValue, maxValue, onConfirm, onClose]);

    // Gestione annulla
    const handleCancel = useCallback(() => {
        setInputValue('');
        setIsReplaceMode(true);
        onClose();
    }, [onClose]);

    // Valore da visualizzare
    const displayValue = inputValue === '' ? currentValue.toString() : inputValue;
    const numericValue = parseInt(displayValue, 10) || 0;
    const isOverMax = numericValue > maxValue;

    return (
        <Modal
            visible={visible}
            transparent
            animationType="fade"
            onRequestClose={handleCancel}
        >
            <View style={styles.overlay}>
                <View style={styles.container}>
                    {/* Header */}
                    <View style={[styles.header, { backgroundColor: color }]}>
                        <Text style={styles.headerText}>{teamName}</Text>
                        <Text style={styles.headerSubtext}>Inserisci punteggio</Text>
                    </View>

                    {/* Display valore */}
                    <View style={styles.displayContainer}>
                        <Text style={[
                            styles.displayValue,
                            { color: isOverMax ? '#f44336' : color }
                        ]}>
                            {displayValue}
                        </Text>
                        {isOverMax && (
                            <Text style={styles.warningText}>Max: {maxValue}</Text>
                        )}
                    </View>

                    {/* Griglia numpad */}
                    <View style={styles.numpadGrid}>
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((digit) => (
                            <TouchableOpacity
                                key={digit}
                                style={styles.numpadButton}
                                onPress={() => handleDigit(digit)}
                            >
                                <Text style={styles.numpadButtonText}>{digit}</Text>
                            </TouchableOpacity>
                        ))}

                        {/* Riga inferiore: DEL, 0, OK */}
                        <TouchableOpacity
                            style={[styles.numpadButton, styles.actionButton, styles.deleteButton]}
                            onPress={handleDelete}
                        >
                            <Text style={styles.deleteButtonText}>DEL</Text>
                        </TouchableOpacity>

                        <TouchableOpacity
                            style={styles.numpadButton}
                            onPress={() => handleDigit(0)}
                        >
                            <Text style={styles.numpadButtonText}>0</Text>
                        </TouchableOpacity>

                        <TouchableOpacity
                            style={[styles.numpadButton, styles.actionButton, styles.confirmButton]}
                            onPress={handleConfirm}
                        >
                            <Text style={styles.confirmButtonText}>OK</Text>
                        </TouchableOpacity>
                    </View>

                    {/* Pulsante annulla */}
                    <TouchableOpacity
                        style={styles.cancelButton}
                        onPress={handleCancel}
                    >
                        <Text style={styles.cancelButtonText}>Annulla</Text>
                    </TouchableOpacity>
                </View>
            </View>
        </Modal>
    );
};

const styles = StyleSheet.create({
    overlay: {
        flex: 1,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        justifyContent: 'center',
        alignItems: 'center',
    },
    container: {
        backgroundColor: '#fff',
        borderRadius: 20,
        width: '85%',
        maxWidth: 320,
        overflow: 'hidden',
        boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.25)',
    },
    header: {
        paddingVertical: 16,
        paddingHorizontal: 20,
        alignItems: 'center',
    },
    headerText: {
        color: '#fff',
        fontSize: 18,
        fontWeight: 'bold',
    },
    headerSubtext: {
        color: 'rgba(255, 255, 255, 0.8)',
        fontSize: 13,
        marginTop: 2,
    },
    displayContainer: {
        paddingVertical: 24,
        alignItems: 'center',
        borderBottomWidth: 1,
        borderBottomColor: '#e0e0e0',
    },
    displayValue: {
        fontSize: 52,
        fontWeight: 'bold',
    },
    warningText: {
        color: '#f44336',
        fontSize: 12,
        marginTop: 4,
    },
    numpadGrid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        padding: 12,
        justifyContent: 'center',
    },
    numpadButton: {
        width: '28%',
        aspectRatio: 1.3,
        margin: '2%',
        backgroundColor: '#f5f5f5',
        borderRadius: 12,
        alignItems: 'center',
        justifyContent: 'center',
    },
    numpadButtonText: {
        fontSize: 28,
        fontWeight: '600',
        color: '#333',
    },
    actionButton: {
        backgroundColor: '#e0e0e0',
    },
    deleteButton: {
        backgroundColor: '#ffebee',
    },
    deleteButtonText: {
        fontSize: 16,
        fontWeight: 'bold',
        color: '#f44336',
    },
    confirmButton: {
        backgroundColor: '#e8f5e9',
    },
    confirmButtonText: {
        fontSize: 18,
        fontWeight: 'bold',
        color: '#4caf50',
    },
    cancelButton: {
        paddingVertical: 16,
        alignItems: 'center',
        borderTopWidth: 1,
        borderTopColor: '#e0e0e0',
    },
    cancelButtonText: {
        color: '#757575',
        fontSize: 16,
    },
});

export default NumpadModal;
